import { useState } from 'react';
import { type LayoutChangeEvent, View } from 'react-native';
import Svg, { Circle, Line, Path } from 'react-native-svg';
import { useThemeColors } from 'smoothie';

const PADDING = { top: 0, right: 0, bottom: 0, left: 0 };
const CHART_HEIGHT = 88;

interface BudgetGraphSimulatorProps {
    budgetCents: number;
    dayOfMonth: number;
    width?: number;
    height?: number;
    lineColor?: string;
    lineThickness?: number;
}

const scaleY = (amount: number, maxScale: number): number => {
    const t = amount / (maxScale || 1);
    return CHART_HEIGHT - t * CHART_HEIGHT;
};

const generateSmoothPath = (
    points: Array<{ x: number; y: number }>,
): string => {
    if (points.length === 0) return '';
    const first = points[0];
    if (first == null) return '';
    if (points.length === 1) return `M ${first.x} ${first.y}`;

    let d = `M ${first.x} ${first.y}`;
    for (let i = 1; i < points.length; i++) {
        const p = points[i];
        const prev = points[i - 1];
        if (p == null || prev == null) continue;
        const cpx = prev.x + (p.x - prev.x) / 2;
        d += ` C ${cpx} ${prev.y}, ${cpx} ${p.y}, ${p.x} ${p.y}`;
    }
    return d;
};

/**
 * A standalone budget graph that simulates spend data for demo/preview purposes.
 * No hooks or real data — just takes a budget and a "current day" and fakes
 * a realistic-looking spend line up to that point.
 */
export const BudgetGraphSimulator = ({
    budgetCents,
    dayOfMonth,
    width,
    height = 100,
    lineColor,
    lineThickness = 2,
}: BudgetGraphSimulatorProps) => {
    const { getColor } = useThemeColors();
    const resolvedLineColor = lineColor ?? getColor('$fruitfulGreen');
    const [measuredWidth, setMeasuredWidth] = useState(0);
    const resolvedWidth = width ?? measuredWidth;

    const handleLayout = (event: LayoutChangeEvent) => {
        setMeasuredWidth(event.nativeEvent.layout.width);
    };

    const chart =
        resolvedWidth > 0 ? (
            <BudgetGraphSimulatorChart
                budgetCents={budgetCents}
                dayOfMonth={dayOfMonth}
                width={resolvedWidth}
                height={height}
                lineColor={resolvedLineColor}
                lineThickness={lineThickness}
            />
        ) : null;

    if (width != null) {
        return chart;
    }

    return (
        <View style={{ width: '100%' }} onLayout={handleLayout}>
            {chart}
        </View>
    );
};

const BudgetGraphSimulatorChart = ({
    budgetCents,
    dayOfMonth,
    width,
    height,
    lineColor,
    lineThickness,
}: Required<
    Pick<
        BudgetGraphSimulatorProps,
        | 'budgetCents'
        | 'dayOfMonth'
        | 'width'
        | 'height'
        | 'lineColor'
        | 'lineThickness'
    >
>) => {
    const { getColor } = useThemeColors();
    const daysInMonth = 30;
    const graphWidth = width - PADDING.left - PADDING.right;
    const rightEdge = PADDING.left + graphWidth;

    const budgetAtDayOne = budgetCents * (1 / daysInMonth);
    const x0 = PADDING.left;
    const x1 = rightEdge;
    const y0 = scaleY(budgetAtDayOne, budgetCents);
    const y1 = scaleY(budgetCents, budgetCents);
    const yBottom = CHART_HEIGHT;

    const budgetAreaPath = `M ${x0} ${y0} L ${x1} ${y1} L ${x1} ${yBottom} L ${x0} ${yBottom} Z`;

    const gridLineDays = [7, 14, 21, 28];
    const gridLineXPositions = gridLineDays.map((day) => {
        const t = (day - 1) / (daysInMonth - 1);
        return PADDING.left + t * graphWidth;
    });

    const spendPoints: Array<{ x: number; y: number }> = [];
    const currentSpend = budgetCents * (dayOfMonth / daysInMonth) * 0.55;

    for (let day = 1; day <= dayOfMonth; day++) {
        const t = (day - 1) / (daysInMonth - 1);
        const x = PADDING.left + t * graphWidth;

        const progress = day / dayOfMonth;
        const jitter = 1 + Math.sin(day * 1.7) * 0.08;
        const amount = currentSpend * progress * jitter;

        const y = scaleY(Math.min(amount, budgetCents), budgetCents);
        spendPoints.push({ x, y: Math.max(8, y) });
    }

    const spendPath =
        spendPoints.length >= 2 ? generateSmoothPath(spendPoints) : '';
    const lastPoint = spendPoints[spendPoints.length - 1];

    return (
        <Svg width={width} height={height}>
            <Path d={budgetAreaPath} fill={getColor('$leafLight')} />
            {gridLineXPositions.map((x, i) => (
                <Line
                    key={i}
                    x1={x}
                    y1={0}
                    x2={x}
                    y2={CHART_HEIGHT}
                    stroke="rgba(0,0,0,0.05)"
                    strokeWidth={1}
                />
            ))}
            {spendPath !== '' && (
                <Path
                    d={spendPath}
                    fill="none"
                    stroke={lineColor}
                    strokeWidth={lineThickness}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            )}
            {lastPoint != null && (
                <Circle
                    cx={lastPoint.x}
                    cy={lastPoint.y}
                    r={6}
                    fill="white"
                    stroke={lineColor}
                    strokeWidth={2}
                />
            )}
        </Svg>
    );
};
